import { appMode } from '@/utils/config/mode'
import { findDuplicates } from '@/utils/helpers/findDuplicates'
import _ from '@/utils/setup/lodash'
import account from './account'
import alerts from './alerts'
import apiErrors from './api-errors'
import cities from './cities'
import cloudAccounts from './cloud-accounts'
import cloudCostManagement from './cloud-cost-management'
import credentials from './credentials'
import environment from './environment'
import errors from './errors'
import events from './events'
import externalBackends from './external-backends'
import filters from './filters'
import forms from './forms'
import infra from './infra'
import inventory from './inventory'
import kpis from './kpis'
import lang from './lang'
import legal from './legal'
import licence from './licence'
import members from './members'
import pipeline from './pipeline'
import plans from './plans'
import plugins from './plugins'
import pluralizations from './pluralizations'
import presentation from './presentation'
import roles from './roles'
import routes from './routes'
import severities from './severities'
import sorts from './sorts'
import stacks from './stacks'
import terracost from './terracost'
import time from './time'
import watchRules from './watchRules'
import workers from './workers'

const commonCycloidTerms = {
  Activity: 'Actividad',
  APIKey: 'Clave API',
  APIKeys: 'Claves API',
  Appearance: 'Apariencia',
  CatalogRepositories: 'Repositorio de catálogos',
  CatalogRepository: 'Repositorio del catálogo',
  Component: 'Componente',
  ConfigRepositories: 'Repositorios de configuración',
  ConfigRepository: 'Repositorio de configuración',
  Credential: 'Credencial',
  Credentials: 'Credenciales',
  Dashboard: 'Panel de control',
  Details: 'Detalles',
  EnvComponent: 'Componente',
  EnvComponents: 'Componentes',
  CloudAccounts: 'Cuentas en la nube',
  Environment: 'Entorno',
  Environments: 'Entornos',
  Events: 'Eventos',
  InfraPolicy: 'InfraPolicy',
  InfraPolicies: 'InfraPolicies',
  Infrastructure: 'Infraestructura',
  Inventory: 'Inventario',
  Member: 'Miembro',
  Members: 'Miembros',
  Metrics: 'Métricas',
  Organization: 'Organizacion',
  Organizations: 'Organizaciones',
  Pending: 'Pendiente',
  Project: 'Proyecto',
  Projects: 'Proyectos',
  Resource: 'Recurso',
  Resources: 'Recursos',
  Role: 'Rol',
  Roles: 'Roles',
  Stack: 'Stack',
  Stacks: 'Stacks',
  Team: 'Equipo',
  Teams: 'Equipos',
  TerraformBackend: 'Backend de Terraform',
  Users: 'Usuarios',
  Output: 'Salida',
  Outputs: 'Salidas',
}

// Add the same set of terms with camelCased keys and lowercased values.
const camelCaseTerms = _.mapValues(_.$camelCaseKeys(commonCycloidTerms), _.toLower)
Object.assign(commonCycloidTerms, camelCaseTerms)

const es = {
  all: 'Todas',
  about: 'Acerca de',
  and: 'y',
  assignOwner: 'Asignar propietario',
  Author: 'Autor',
  clone: 'Clonar',
  color: 'Color',
  collapse: 'colapsar',
  copy: 'Copiar',
  copyToClipboard: 'Copiar al portapapeles',
  comingSoon: 'próximamente',
  comparedToLastWeek: 'Comparado con la semana pasada',
  configuration: 'Configuración',
  configure: 'Configurar',
  date: 'Fecha',
  Default: 'Por defecto',
  development: 'desarrollo',
  disabled: 'Desactivada',
  editor: 'Editor',
  enabled: 'Activada',
  Filters: 'Filtros',
  from: 'de',
  fullScreen: 'Pantalla completa',
  general: 'General',
  icon: 'Icono',
  importing: 'importando',
  invite: 'Invitar',
  learnMore: 'Aprenda más',
  maintainer: 'Mantenedor',
  name: 'Nombre',
  noOwner: 'sin propietario',
  or: 'o',
  other: 'Otro',
  others: 'Otros',
  owner: 'Propietario',
  preview: 'Vista previa',
  properties: 'Propiedades',
  provider: 'Proveedor',
  ReadMore: 'Leer más',
  seeMore: 'ver más',
  Settings: 'Ajustes',
  Summary: 'Resumen',
  weekOf: 'Semana del',
  welcomeMsg: 'Bienvenido a la App de Cycloid',
  youAreUsing: 'Está utilizando',
  youDoNotHavePermissionsTo: 'No tiene permisos suficientes para',
  info: 'Información',
  warning: 'Advertencia',
  error: 'Error',
  critical: 'Critical',
  read: 'Leído',
  unread: 'No leído',
  items: 'elementos',
  lastUsed: 'Último uso',
  ...commonCycloidTerms,
  ...pluralizations,
}

// We flatten the imported objects in order to avoid sneaky clashes
// with previously defined strings (see FE#7160).
const moduleTranslations = {
  account: account.es,
  alerts: alerts.es,
  apiErrors: apiErrors.es,
  cities: cities.en,
  cloudAccounts: cloudAccounts.es,
  cloudCostManagement: cloudCostManagement.es,
  credentials: credentials.es,
  environment: environment.es,
  errors: errors.es,
  events: events.es,
  externalBackends: externalBackends.es,
  filters: filters.es,
  forms: forms.es,
  infra: infra.es,
  inventory: inventory.es,
  kpis: kpis.es,
  lang: lang.es,
  legal: legal.es,
  licence: licence.es,
  members: members.es,
  pipeline: pipeline.es,
  plans: plans.es,
  presentation: presentation.es,
  roles: roles.es,
  routes: routes.es,
  severities: severities.es,
  sorts: sorts.es,
  stacks: stacks.es,
  terracost: terracost.es,
  time: time.es,
  watchRules: watchRules.es,
  workers: workers.es,
  plugins: plugins.es,
}

// Final check for duplicate keys before exporting all translations (FE#7168).
const duplicates = findDuplicates([...Object.keys(es), ...Object.keys(moduleTranslations)])
if (duplicates.length && appMode.isDev) {
  console.warn(`Duplicate keys found in translations export: ${duplicates.join(', ')}`)
}

export default {
  ...es,
  ...moduleTranslations,
}
